import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dahgcdjoh653ivfvnubg";

export default function NewsletterForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "订阅失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-md border border-accent-400/50 bg-accent-500/10 px-4 py-3 text-sm text-background-100">
        <span className="flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-accent-300" aria-hidden="true" />
          订阅成功，感谢关注！每日热点会优先送达你的邮箱。
        </span>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="newsletter-form"
      onSubmit={handleSubmit}
      className="space-y-3"
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="输入邮箱，订阅每日热点"
          className="w-full rounded-md border border-background-700/60 bg-background-50/95 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/40"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? "提交中…" : "立即订阅"}
        </button>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="anti-spam-field"
      />

      {status === "error" && formError ? (
        <p className="flex items-center gap-1.5 text-xs text-accent-300">
          <i className="ri-error-warning-line" aria-hidden="true" />
          {formError}
        </p>
      ) : (
        <p className="text-xs text-background-300">我们只发送内容更新，随时可以取消订阅。</p>
      )}
    </form>
  );
}